import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import { loadTenant } from "../../lib/tenant";
import { useNavigate } from "react-router-dom";

const PLUMBING_FACTS = [
  "Copper naturally inhibits bacterial growth, which is why it's trusted in potable water systems.",
  "Most PVC failures happen at joints, not in the pipe — surface prep matters more than force.",
  "Thread seal tape lubricates threads so they seat properly — it doesn't magically stop leaks.",
  "Over-tightening fittings causes more leaks than under-tightening, especially on plastic threads.",
  "Some brass fittings can weaken over time due to dezincification in aggressive water conditions.",
  "PEX was introduced in Europe in the 1970s and took decades to become mainstream elsewhere.",
  "Water hammer isn't just noise — repeated shock shortens the life of valves and appliances.",
  "Tempering valves reduce scalding risk by controlling hot water delivery temperature.",
  "Flow rate drops quickly with restrictions — small bends and fittings add up fast.",
  "Isolation valves turn emergency jobs into five-minute fixes.",
  "A dripping tap can waste more than 20,000 litres of water a year — small leaks add up fast.",
  "Hot water systems should be set to 60°C at the storage tank to prevent Legionella growth.",
  "Most burst pipes in winter fail at bends and joints, not the straight runs.",
  "Backflow prevention devices protect the mains water supply from contamination — they're not optional.",
  "The average shower uses 9 litres per minute; a low-flow head can cut that nearly in half.",
  "PEX expands rather than cracks in freezing conditions, making it ideal for cold climates.",
  "Flux left on copper fittings after soldering causes long-term corrosion — always clean it off.",
  "Gate valves are for isolation only — use ball valves anywhere you need reliable shutoff.",
  "Stainless steel corrodes faster than you'd expect when in contact with dissimilar metals.",
  "Most water meters have a small leak indicator dial — one of the fastest ways to detect hidden leaks.",
  "Push-fit fittings are designed for clean, square cuts — a ragged edge is the most common cause of failure.",
  "A co-op model means profits go back to members, not shareholders — your purchases build your dividend.",
  "Plumbers Co-op has been supporting the trade since 1962 — over 60 years of Australian know-how.",
  "Buying through the Co-op means access to trade pricing that independent operators can't always get elsewhere.",
  "The right thread lubricant makes a real difference — PTFE tape works differently on BSP versus NPT threads.",
  "Australia's WaterMark certification ensures plumbing products meet national safety and performance standards.",
  "In Australia, all plumbing work on sanitary drainage must be done by a licensed plumber.",
  "Grey water systems can legally reuse shower and laundry water for garden irrigation in most Australian states.",
  "The National Construction Code sets minimum standards for all plumbing installations across Australia.",
  "Flexi hoses have a limited lifespan of around 10 years — they're one of the leading causes of home water damage.",
  "Pressure reducing valves protect downstream fixtures — anything above 500kPa can cause appliance failure.",
  "A blocked trap is the most common cause of bad smells from drains — it dries out when not used regularly.",
  "Co-ops are owned by their members, meaning every purchase contributes to the collective buying power of the trade.",
  "Silicone sealant and plumber's putty are not interchangeable — putty degrades some plastics over time.",
  "Solar hot water systems work best when the collector faces north in Australia and is angled at roughly your latitude.",
  "A 1mm crack in a pipe can discharge up to 400 litres of water per hour.",
  "The P-trap under a sink holds water to create a seal against sewer gases — it must always remain full.",
  "Lead was used in Australian plumbing solder until the 1980s — older homes may still have traces in their pipes.",
  "Thermostatic mixing valves must be set and tested at commissioning — a poorly set TMV is a safety risk.",
  "Poly pipe fittings need the correct insert stiffener for full performance — skipping it risks joint failure under pressure.",
  "Cast iron drainage pipes can last over 100 years when properly maintained — they're still common in older commercial buildings.",
  "The AS/NZS 3500 plumbing standard is the benchmark for all water services and drainage work in Australia.",
  "Expansion loops are essential in long hot water pipe runs — without them, thermal movement causes stress fractures.",
  "A ball valve that hasn't been operated in years can seize — exercising valves annually keeps them reliable.",
  "Water pressure at the meter and at the fixture can differ significantly depending on pipe size and run length.",
  "Copper tube grades differ — Type B is standard for water supply, Type D is for gas.",
  "HDPE pipe is highly resistant to chemicals and abrasion, making it the preferred choice for underground drainage.",
  "Grease traps must be regularly pumped — blocked traps cause drainage failure and fines for commercial premises.",
  "The member dividend at Plumbers Co-op is calculated on your net purchases — the more you buy, the more you earn back.",
  "Joining the Co-op isn't just about pricing — members get access to exclusive product ranges and supplier relationships.",
  "AS 3788 requires periodic inspection and testing of pressure vessels, including hot water storage systems.",
  "Sewer gas contains hydrogen sulfide, which is toxic in high concentrations — always ensure proper venting.",
  "A correctly graded drain falls 1 in 60 — too steep and solids are left behind; too flat and blockages build up.",
  "Combination tempering valves simplify hot water compliance by integrating both the tempering and check valve functions.",
  "An uninsulated hot water pipe loses heat rapidly — lagging reduces energy loss and speeds up delivery at the tap.",
  "First fix plumbing sets up all the rough-in work; second fix covers the visible fittings and fixtures.",
  "WaterMark certification applies to the product, not just the manufacturer — always check the label on the fitting itself.",
  "Low water pressure complaints are often caused by partially closed isolation valves, not the supply itself.",
  "Trenchless pipe relining can repair deteriorated drainage without excavation — a growing option for older commercial sites.",
  "The Co-op difference: every branch is run for members, not for profit margins — service and stock availability come first.",
];

function getFactOfTheDay(facts) {
  if (!facts.length) return "";
  const today = new Date().toISOString().slice(0, 10);
  let hash = 0;
  for (let i = 0; i < today.length; i++) {
    hash = (hash * 31 + today.charCodeAt(i)) >>> 0;
  }
  return facts[hash % facts.length];
}

export default function Dashboard() {
  const navigate = useNavigate();

  const [theme, setTheme] = useState({
    primaryColor: "#fa8128",
    secondaryColor: "#000000",
    bannerUrl: "/dashboardbanner.png",
  });

  const [firstName, setFirstName] = useState("");
  const [assignedCourses, setAssignedCourses] = useState([]);
  const [completedCourses, setCompletedCourses] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [plumbingFact] = useState(() => getFactOfTheDay(PLUMBING_FACTS));

  useEffect(() => {
    try {
      const stored = localStorage.getItem("vibeTheme");
      if (stored) {
        const parsed = JSON.parse(stored);
        setTheme((prev) => ({ ...prev, ...parsed }));
      }
    } catch {}
  }, []);

  const safeReadJson = async (resp) => {
    try { return await resp.json(); } catch { return null; }
  };

  const syncAndLoad = useCallback(async () => {
    setSyncing(true);
    try {
      const { data: userRes } = await supabase.auth.getUser();
      const user = userRes?.user;
      if (!user?.id) return;
      const userId = user.id;

      // First name
      const { data: profileRows, error: profileError } = await supabase
        .from("profiles")
        .select("first_name")
        .eq("user_id", userId);
      if (profileError) console.error("Profile fetch error:", profileError);
      const name = profileRows?.[0]?.first_name?.trim();
      setFirstName(name || "there");

      // Load tenant (same as Courses.jsx)
      const tenant = await loadTenant();
      if (!tenant?.id) return;

      // Load courses catalogue (same query as Courses.jsx)
      const { data: coursesData } = await supabase
        .from("courses")
        .select("id, title")
        .eq("tenant_id", tenant.id)
        .eq("active", true);

      const courses = coursesData || [];

      // Load enrolments (same query as Courses.jsx)
      const { data: enrolsData } = await supabase
        .from("enrolments")
        .select("id, course_id, scormcloud_registration_id, completed, completed_at")
        .eq("tenant_id", tenant.id)
        .eq("user_id", userId);

      const enrolments = enrolsData || [];

      // Sync incomplete SCORM registrations (same as original Dashboard)
      for (const e of enrolments.filter((x) => !x.completed && x.scormcloud_registration_id)) {
        try {
          const resp = await fetch("/api/scorm/progress", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ registrationId: e.scormcloud_registration_id }),
          });
          if (!resp.ok) continue;
          const progress = await safeReadJson(resp);
          const status = String(progress?.status || "").toLowerCase();
          if (status === "complete" || status === "completed") {
            await supabase
              .from("enrolments")
              .update({ completed: true, completed_at: new Date().toISOString() })
              .eq("id", e.id);
            e.completed = true;
          }
        } catch {}
      }

      // Cross-reference exactly like Courses.jsx does
      const enrolmentByCourseId = new Map();
      for (const e of enrolments) enrolmentByCourseId.set(e.course_id, e);

      const assigned = courses
        .filter((c) => {
          const e = enrolmentByCourseId.get(c.id);
          return e && !e.completed;
        })
        .map((c) => ({ id: c.id, title: c.title }));

      const completed = courses
        .filter((c) => enrolmentByCourseId.get(c.id)?.completed)
        .map((c) => ({ id: c.id, title: c.title }));

      setAssignedCourses(assigned);
      setCompletedCourses(completed);
    } finally {
      setSyncing(false);
    }
  }, []);

  useEffect(() => {
    syncAndLoad();
    const onFocus = () => syncAndLoad();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [syncAndLoad]);

  const total = assignedCourses.length + completedCourses.length;
  const pct = total > 0 ? Math.round((completedCourses.length / total) * 100) : 0;

  return (
    <div className="p-6 space-y-6">

      {/* Banner */}
      <div
        className="rounded-2xl overflow-hidden w-full shadow-md h-40 sm:h-56 lg:h-64 bg-gray-200"
        style={{
          backgroundImage: `url(${theme.bannerUrl || "/dashboardbanner.png"})`,
          backgroundSize: "cover",
          backgroundPosition: "left center",
        }}
      />

      {/* Welcome / Fact */}
      <div className="bg-white shadow-md rounded-xl p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-1">
          Hello, {firstName || "…"}
        </h1>
        <p className="text-xs font-semibold tracking-wide text-gray-400 uppercase mb-1">
          Fact of the day
        </p>
        <p className="text-gray-600">{plumbingFact}</p>
        {syncing && (
          <p className="text-xs text-gray-400 mt-2">Syncing course completion…</p>
        )}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

        {/* Assigned courses */}
        <div className="bg-white rounded-xl shadow-md p-6 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{assignedCourses.length}</p>
                <p className="text-sm text-gray-500">
                  {assignedCourses.length === 1 ? "Course to complete" : "Courses to complete"}
                </p>
              </div>
            </div>
            {assignedCourses.length > 0 ? (
              <span className="flex-shrink-0 text-xs font-medium px-3 py-1 rounded-full bg-amber-100 text-amber-700">
                Action required
              </span>
            ) : (
              <span className="flex-shrink-0 text-xs font-medium px-3 py-1 rounded-full bg-emerald-100 text-emerald-700">
                All caught up!
              </span>
            )}
          </div>

          {assignedCourses.length > 0 ? (
            <div className="space-y-2 mt-1 overflow-y-auto max-h-64 pr-1">
              {assignedCourses.map((course) => (
                <button
                  key={course.id}
                  onClick={() => navigate("/learner/courses?tab=assigned")}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-amber-50 hover:bg-amber-100 transition group"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                    <p className="text-sm text-gray-800 font-medium truncate text-left">{course.title}</p>
                  </div>
                  <span className="text-xs text-amber-600 font-medium flex-shrink-0 ml-2 group-hover:underline">
                    Start →
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center py-4">
              <p className="text-sm text-gray-400 italic">No outstanding courses — great work!</p>
            </div>
          )}
        </div>

        {/* Completed courses */}
        <div className="bg-white rounded-xl shadow-md p-6 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{completedCourses.length}</p>
                <p className="text-sm text-gray-500">
                  {completedCourses.length === 1 ? "Course completed" : "Courses completed"}
                </p>
              </div>
            </div>
          </div>

          {total > 0 && (
            <div className="mb-4">
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>Overall progress</span>
                <span>{pct}%</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1.5">
                <div
                  className="bg-emerald-500 h-1.5 rounded-full transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )}

          {completedCourses.length > 0 ? (
            <div className="space-y-2 overflow-y-auto max-h-64 pr-1">
              {completedCourses.map((course) => (
                <div
                  key={course.id}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-emerald-50"
                >
                  <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-sm text-gray-700 font-medium truncate">{course.title}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center py-4">
              <p className="text-sm text-gray-400 italic">No completed courses yet — your first is waiting!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
